import { Star } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

type Review = { quote: string; name: string; company: string; rating: number };

function StarRow({ n }: { n: number }) {
  return (
    <div style={{ display: "flex", gap: 2, marginBottom: 10 }}>
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={12} style={{ fill: "var(--lavender-400)", color: "var(--lavender-400)" }} />
      ))}
    </div>
  );
}

function Card({ r }: { r: Review }) {
  return (
    <article className="dyna-t__card" style={{ width: 340, flexShrink: 0 }}>
      <p className="dyna-t__text">&ldquo;{r.quote}&rdquo;</p>
      <StarRow n={r.rating} />
      <div className="dyna-t__byline">
        <div className="dyna-t__byline-avatar">{r.name[0]}</div>
        <div>
          <div className="dyna-t__name">{r.name}</div>
          <div className="dyna-t__role">{r.company}</div>
        </div>
      </div>
    </article>
  );
}

export default memo(function TestimonialsSection() {
  const { t } = useTranslation();

  const reviews: Review[] = [
    { quote: t("dyna.testimonials.r1q"), name: t("dyna.testimonials.r1n"), company: t("dyna.testimonials.r1c"), rating: 5 },
    { quote: t("dyna.testimonials.r2q"), name: t("dyna.testimonials.r2n"), company: t("dyna.testimonials.r2c"), rating: 5 },
    { quote: t("dyna.testimonials.r3q"), name: t("dyna.testimonials.r3n"), company: t("dyna.testimonials.r3c"), rating: 5 },
    { quote: t("dyna.testimonials.r4q"), name: t("dyna.testimonials.r4n"), company: t("dyna.testimonials.r4c"), rating: 5 },
    { quote: t("dyna.testimonials.r5q"), name: t("dyna.testimonials.r5n"), company: t("dyna.testimonials.r5c"), rating: 5 },
    { quote: t("dyna.testimonials.r6q"), name: t("dyna.testimonials.r6n"), company: t("dyna.testimonials.r6c"), rating: 5 },
    { quote: t("dyna.testimonials.r7q"), name: t("dyna.testimonials.r7n"), company: t("dyna.testimonials.r7c"), rating: 5 },
    { quote: t("dyna.testimonials.r8q"), name: t("dyna.testimonials.r8n"), company: t("dyna.testimonials.r8c"), rating: 5 },
  ];

  // 3x clone so -33.333% lands on an exact repeat — no jump at the loop point
  const track = [...reviews, ...reviews, ...reviews];

  return (
    <section
      id="testimonials"
      className="dyna-section--alt"
      style={{ padding: "var(--s-lg) 0", overflow: "hidden", position: "relative" }}
    >
      {/* Section header */}
      <div style={{ textAlign: "center", marginBottom: 48, padding: "0 var(--site-px)" }}>
        <h2 style={{
          fontFamily: "Coolvetica, system-ui, sans-serif",
          fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 700,
          letterSpacing: "0.05em", textTransform: "uppercase",
          lineHeight: 1.1, margin: "0 0 12px", color: "var(--dyna-fg)",
        }}>
          {t("dyna.testimonials.section_header")}
        </h2>
      </div>

      {/* Horizontal marquee — mirrors the portfolio section */}
      <div
        style={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
        }}
      >
        <div
          className="testimonials-track"
          style={{ gap: 20, willChange: "transform" }}
          onMouseEnter={e => (e.currentTarget.style.animationPlayState = "paused")}
          onMouseLeave={e => (e.currentTarget.style.animationPlayState = "running")}
        >
          {track.map((r, i) => <Card key={i} r={r} />)}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .testimonials-track {
          display: flex;
          width: max-content;
          animation: marquee-ltr 45s linear infinite;
        }
        [dir="rtl"] .testimonials-track {
          animation: marquee-rtl 45s linear infinite;
        }
        @keyframes marquee-ltr {
          from { transform: translateX(0); }
          to   { transform: translateX(-33.333%); }
        }
        @keyframes marquee-rtl {
          from { transform: translateX(0); }
          to   { transform: translateX(33.333%); }
        }

        [data-theme="light"] .dyna-t__card {
          background: radial-gradient(ellipse at 50% 0%, rgba(135,115,180,0.10), transparent 55%), linear-gradient(180deg, #ffffff, #f7f2fc);
          border-color: rgba(90,70,102,0.12);
          box-shadow: 0 8px 32px -8px rgba(135,115,180,0.14), 0 2px 8px rgba(90,70,102,0.07);
        }
        [data-theme="light"] .dyna-t__text { color: var(--nova-600); }
        [data-theme="light"] .dyna-t__name { color: var(--nova-600); }
      `,
        }}
      />
    </section>
  );
});
